// @ts-nocheck
// DEPRECATED: This hook is part of the old navigation system
// It has been replaced by the simple onNext/onBack pattern with useStepRedirect hook
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../features/auth/contexts/AuthContext';
import { useDoctorInfo } from '../features/registration/contexts/DoctorInfoContext';
import {
  navigationService,
  RegistrationStep,
  RegistrationSection,
  NavigationValidationResult
} from '../shared/api/services/NavigationService';

interface UseRegistrationNavigationResult {
  currentStep: string | null;
  currentSection: RegistrationSection | null;
  isLoading: boolean;
  error: string | null;
  navigateToStep: (targetStep: string, options?: { force?: boolean }) => Promise<boolean>;
  canNavigateToStep: (targetStep: string) => Promise<boolean>;
  refreshProgress: () => Promise<void>;
  clearError: () => void;
  navigateAfterStepSubmission: (nextStep?: string) => Promise<void>;
}

interface NavigationOptions {
  force?: boolean;
  skipConfirmation?: boolean;
}

export function useRegistrationNavigation(): UseRegistrationNavigationResult {
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [currentSection, setCurrentSection] = useState<RegistrationSection | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { doctorData } = useAuthContext();
  const { refreshDoctorInfo } = useDoctorInfo();

  // Refresh current progress from doctor data
  const refreshProgress = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!doctorData?.registration_progress) {
        setCurrentStep(RegistrationStep.SCAN_ID);
        setCurrentSection(RegistrationSection.ID_FACE);
        return;
      }

      const step = doctorData.registration_progress;

      // Check if doctor has full_name (ID scanned)
      const hasFullName = !!doctorData.full_name;

      // Check if face verification photo exists
      const hasFacePhoto = !!(doctorData.documents?.face_verification_photo);

      // Resolve effective step considering verification flags
      const effectiveStep = navigationService.resolveEffectiveStep(
        step,
        hasFullName,
        hasFacePhoto
      );

      const section = navigationService.getRegistrationSection(effectiveStep);

      setCurrentStep(effectiveStep);
      setCurrentSection(section);
    } catch (err) {
      console.error('Failed to refresh progress:', err);
      setError('Failed to refresh registration progress');
    } finally {
      setIsLoading(false);
    }
  }, [doctorData?.registration_progress, doctorData?.full_name, doctorData?.documents?.face_verification_photo]);

  // Initialize on mount and when doctor data changes
  useEffect(() => {
    refreshProgress();
  }, [doctorData?.registration_progress]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Show confirmation dialog for progress reset
  const showConfirmationDialog = useCallback(async (message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      // Simple confirm dialog - replace with your preferred UI component
      const confirmed = window.confirm(message);
      resolve(confirmed);
    });
  }, []);

  // Check if navigation to a step is allowed
  const canNavigateToStep = useCallback(async (targetStep: string): Promise<boolean> => {
    try {
      setError(null);
      
      const validation = await navigationService.validateNavigation(targetStep);
      
      if (!validation.isSuccess) {
        setError(validation.error || 'Navigation validation failed');
        return false;
      }

      return validation.data!.can_navigate;
    } catch (err) {
      console.error('Navigation validation error:', err);
      setError('Failed to validate navigation');
      return false;
    }
  }, []);

  // Navigate to a specific step with validation
  const navigateToStep = useCallback(async (
    targetStep: string, 
    options: NavigationOptions = {}
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      // If force is true, skip validation
      if (options.force) {
        const route = navigationService.getStepRoute(targetStep);
        navigate(route, { replace: true });
        return true;
      }

      // Use the navigation service to validate and handle confirmation
      const result = await navigationService.canNavigateToWithConfirmation(
        targetStep,
        options.skipConfirmation ? undefined : showConfirmationDialog
      );

      if (!result.canNavigate) {
        setError(result.error || 'Navigation not allowed');
        return false;
      }

      // Navigation is allowed, perform the navigation
      const route = navigationService.getStepRoute(targetStep);
      navigate(route, { replace: true });
      
      // Don't call refreshProgress here as it will be triggered by doctorData change
      
      return true;
    } catch (err) {
      console.error('Navigation error:', err);
      setError('Navigation failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [navigate, showConfirmationDialog]);

  // Navigate after step submission - ensures doctor info is refreshed first
  const navigateAfterStepSubmission = useCallback(async (nextStep?: string): Promise<void> => {
    console.log('🔄 navigateAfterStepSubmission called', { nextStep });

    try {
      setIsLoading(true);
      setError(null);

      console.log('📱 Refreshing doctor info to get latest registration progress...');
      // Force refresh doctor info to get the latest registration progress
      await refreshDoctorInfo(true);

      let targetStep = nextStep;

      // If nextStep is not provided, fetch it from doctor data as a fallback
      if (!targetStep) {
        console.log('⚡ No nextStep provided, fetching from doctor data...');
        const freshDoctorInfo = await refreshDoctorInfo(true);
        console.log('📊 Fresh doctor info:', freshDoctorInfo);
        if (freshDoctorInfo?.registration_progress) {
          targetStep = freshDoctorInfo.registration_progress;
          console.log('✅ Found registration progress:', targetStep);
        }
      } else {
        console.log('✅ Using provided nextStep:', targetStep);
      }

      if (!targetStep) {
        console.warn('⚠️ No registration progress found after step submission');
        // Fallback to a default step if progress is missing
        navigate(navigationService.getStepRoute(RegistrationStep.SCAN_ID), { replace: true });
        return;
      }

      // Check if doctor has completed their profile (doctor_documents step)
      // If so, redirect to home instead of continuing with clinic registration
      if (targetStep === RegistrationStep.CHOOSE_DOCTOR_POINT || targetStep === 'choose_doctor_point') {
        console.log('✅ Doctor completed profile, redirecting to home');
        navigate('/home', { replace: true });
        return;
      }

      // Get the correct route for the doctor's current progress
      const targetRoute = navigationService.getStepRoute(targetStep);
      console.log('🧭 Navigation details:', {
        targetStep,
        targetRoute,
        currentLocation: window.location.pathname
      });

      // Navigate to the step indicated by the fresh doctor data
      console.log('🚀 Navigating to:', targetRoute);
      navigate(targetRoute, { replace: true });
      console.log('✅ Navigation completed');
      
    } catch (err) {
      console.error('❌ Navigation after step submission failed:', err);
      setError('Failed to navigate after step submission');
    } finally {
      setIsLoading(false);
      console.log('🏁 navigateAfterStepSubmission finished');
    }
  }, [navigate, refreshDoctorInfo]);

  return {
    currentStep,
    currentSection,
    isLoading,
    error,
    navigateToStep,
    canNavigateToStep,
    refreshProgress,
    clearError,
    navigateAfterStepSubmission
  };
}

// Enhanced hook with additional utilities
export function useRegistrationNavigationWithUtils() {
  const navigation = useRegistrationNavigation();
  const location = useLocation();

  // Get current step from URL if different from doctor data
  const getCurrentStepFromPath = useCallback((): string => {
    return navigationService.getStepFromRoute(location.pathname);
  }, [location.pathname]);

  // Check if current path matches doctor progress
  const isOnCorrectStep = useCallback((): boolean => {
    const pathStep = getCurrentStepFromPath();
    return pathStep === navigation.currentStep;
  }, [getCurrentStepFromPath, navigation.currentStep]);

  // Get section information
  const getSectionInfo = useCallback((section: RegistrationSection) => {
    const steps = navigationService.getSectionSteps(section);
    const currentStepIndex = navigation.currentStep ? steps.indexOf(navigation.currentStep) : -1;
    
    return {
      steps,
      currentStepIndex,
      totalSteps: steps.length,
      isCompleted: currentStepIndex === steps.length - 1,
      progress: currentStepIndex >= 0 ? ((currentStepIndex + 1) / steps.length) * 100 : 0
    };
  }, [navigation.currentStep]);

  // Navigate to next step in sequence
  const navigateToNextStep = useCallback(async (): Promise<boolean> => {
    if (!navigation.currentStep || !navigation.currentSection) {
      return false;
    }

    const sectionSteps = navigationService.getSectionSteps(navigation.currentSection);
    const currentIndex = sectionSteps.indexOf(navigation.currentStep);
    
    if (currentIndex >= 0 && currentIndex < sectionSteps.length - 1) {
      const nextStep = sectionSteps[currentIndex + 1];
      return navigation.navigateToStep(nextStep);
    }

    return false;
  }, [navigation]);

  // Navigate to previous step in sequence
  const navigateToPreviousStep = useCallback(async (): Promise<boolean> => {
    if (!navigation.currentStep || !navigation.currentSection) {
      return false;
    }

    const sectionSteps = navigationService.getSectionSteps(navigation.currentSection);
    const currentIndex = sectionSteps.indexOf(navigation.currentStep);
    
    if (currentIndex > 0) {
      const previousStep = sectionSteps[currentIndex - 1];
      return navigation.navigateToStep(previousStep);
    }

    return false;
  }, [navigation]);

  return {
    ...navigation,
    getCurrentStepFromPath,
    isOnCorrectStep,
    getSectionInfo,
    navigateToNextStep,
    navigateToPreviousStep
  };
} 